import { useState, useEffect, useRef, useCallback } from 'react';
import { beatsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Navigation, MapPin, Clock, Loader2, StopCircle } from 'lucide-react';

const PING_INTERVAL = 30000; // 30 seconds

export function BeatTracker() {
  const { user } = useAuth();
  const [activeBeat, setActiveBeat] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [elapsed, setElapsed] = useState('');
  const pingRef = useRef(null);
  const watchRef = useRef(null);
  const lastPosRef = useRef(null);

  // Check for active beat on mount
  useEffect(() => {
    beatsApi.getMyActive().then(res => {
      if (res.data.beat) {
        setActiveBeat(res.data.beat);
        startTracking();
      }
    }).catch(() => {});
    return () => stopTracking();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Elapsed time ticker
  useEffect(() => {
    if (!activeBeat) { setElapsed(''); return; }
    const tick = () => {
      const start = new Date(activeBeat.started_at);
      const diff = Math.floor((Date.now() - start.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${h > 0 ? h + 'h ' : ''}${m}m ${s}s`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [activeBeat]);

  const sendPing = useCallback(async (lat, lng, speed, heading) => {
    try {
      await beatsApi.ping({ lat, lng, speed, heading, accuracy: null });
    } catch { /* silent — don't alert user */ }
  }, []);

  const startTracking = useCallback(() => {
    // Use watchPosition for continuous GPS
    if (navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          lastPosRef.current = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            speed: pos.coords.speed,
            heading: pos.coords.heading,
          };
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000 }
      );
    }
    // Send pings at interval
    pingRef.current = setInterval(() => {
      if (lastPosRef.current) {
        sendPing(lastPosRef.current.lat, lastPosRef.current.lng, lastPosRef.current.speed, lastPosRef.current.heading);
      }
    }, PING_INTERVAL);
  }, [sendPing]);

  const stopTracking = useCallback(() => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  }, []);

  const handleStart = async () => {
    setStarting(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => reject(new Error('Location access required to start beat')),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });
      const res = await beatsApi.startBeat({ lat: pos.lat, lng: pos.lng, note: '' });
      setActiveBeat({ id: res.data.beat_id, started_at: res.data.started_at, status: 'active' });
      startTracking();
      toast.success('Beat started — location tracking active');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to start beat');
    } finally {
      setStarting(false);
    }
  };

  const handleEnd = async () => {
    setEnding(true);
    try {
      const pos = lastPosRef.current || { lat: 0, lng: 0 };
      await beatsApi.endBeat({ lat: pos.lat, lng: pos.lng, note: '' });
      stopTracking();
      setActiveBeat(null);
      toast.success('Beat ended');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end beat');
    } finally {
      setEnding(false);
    }
  };

  // Only show for salesperson/manager roles
  if (!user || user.role === 'admin' || user.role === 'owner') return null;

  return (
    <div className="mb-4" data-testid="beat-tracker">
      {activeBeat ? (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <Navigation className="w-4 h-4 text-green-600" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </div>
            <span className="text-sm font-medium text-green-800">On Beat</span>
          </div>
          <Badge variant="outline" className="text-green-700 border-green-300 text-xs gap-1">
            <Clock className="w-3 h-3" />
            {elapsed}
          </Badge>
          <span className="text-xs text-green-600">{activeBeat.total_pings || 0} pings</span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto text-red-600 border-red-300 hover:bg-red-50 gap-1"
            onClick={handleEnd}
            disabled={ending}
            data-testid="end-beat-btn"
          >
            {ending ? <Loader2 className="w-3 h-3 animate-spin" /> : <StopCircle className="w-3 h-3" />}
            End Beat
          </Button>
        </div>
      ) : (
        <Button
          onClick={handleStart}
          disabled={starting}
          className="gap-2 bg-green-600 hover:bg-green-700 text-white"
          data-testid="start-beat-btn"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          Start Beat Plan
        </Button>
      )}
    </div>
  );
}
